// Pure helpers for combining defaults with overrides. No React, no I/O.

// Deep-merge `override` onto `base`. Objects merge key-by-key; arrays and
// primitives are replaced wholesale (an overridden list is the full new list).
export function deepMerge<T>(base: T, override: unknown): T {
  if (override === undefined || override === null) return base;
  if (Array.isArray(base) || Array.isArray(override)) {
    return (override ?? base) as T;
  }
  if (
    typeof base === 'object' &&
    base !== null &&
    typeof override === 'object'
  ) {
    const out: Record<string, unknown> = { ...(base as Record<string, unknown>) };
    for (const k of Object.keys(override as Record<string, unknown>)) {
      out[k] = deepMerge(
        (base as Record<string, unknown>)[k],
        (override as Record<string, unknown>)[k]
      );
    }
    return out as T;
  }
  return (override ?? base) as T;
}

// Immutably set `value` at a dotted `path` (e.g. "hero.tagline"), returning a
// new object. Missing intermediate objects are created.
export function setByPath<T extends object>(obj: T, path: string, value: unknown): T {
  const keys = path.split('.');
  const clone = (v: unknown): Record<string, unknown> =>
    Array.isArray(v) ? ([...v] as unknown as Record<string, unknown>) : { ...(v as Record<string, unknown>) };

  const out = clone(obj);
  let cur = out;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cur[k] = clone(cur[k] ?? {});
    cur = cur[k] as Record<string, unknown>;
  }
  cur[keys[keys.length - 1]] = value;
  return out as unknown as T;
}

// Read the value at a dotted `path`, or undefined.
export function getByPath(obj: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, k) => {
    if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[k];
    return undefined;
  }, obj);
}
